import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders } from '../../utils/authUtils';
import { createApiUrl } from '../../utils/apiConfig';
import '../../style.css';

/**
 * DicomPDFViewer - Einfacher PDF-Viewer für DICOM-PDF-Vorschau
 * 
 * Zeigt die vom Backend generierte DICOM-PDF in einem Dialog an.
 * Nur zu Testzwecken - einfache Darstellung ohne komplexe Features.
 */
const DicomPDFViewer = ({ open, onClose, reportId }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [pdfUrl, setPdfUrl] = React.useState(null);
  const [error, setError] = React.useState(null);

  // PDF laden wenn Dialog geöffnet wird
  React.useEffect(() => {
    if (open && reportId) {
      loadPDF();
    }
    
    // Cleanup: URL aufräumen beim Schließen
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [open, reportId]);

  const loadPDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Aktuelle UI-Sprache ermitteln
      const currentLanguage = i18n.language || 'de';
      const language = currentLanguage.split('-')[0]; // 'de-DE' -> 'de'
      
      // PDF vom Backend laden mit Sprach-Parameter
      const apiUrl = createApiUrl(`/reports/${reportId}/dicom-pdf?language=${language}`);
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // PDF als Blob laden
      const blob = await response.blob();
      
      // Blob-URL erstellen
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      
    } catch (err) {
      console.error('Fehler beim Laden der DICOM-PDF:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Cleanup
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
    }
    setPdfUrl(null);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh'
        }
      }}
    >
      <DialogTitle>
        {t('dicomPdfViewer.title', 'DICOM PDF Vorschau')}
      </DialogTitle>
      
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: 400
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        {error && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              minHeight: 400,
              p: 3,
              textAlign: 'center'
            }}
          >
            <div>
              <p style={{ color: 'red', fontWeight: 'bold' }}>
                {t('dicomPdfViewer.error', 'Fehler beim Laden der PDF')}
              </p>
              <p style={{ fontSize: '0.9rem', color: 'var(--rd-text-secondary)' }}>
                {error}
              </p>
            </div>
          </Box>
        )}
        
        {!loading && !error && pdfUrl && (
          <iframe
            src={pdfUrl}
            title="DICOM PDF"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              flex: 1
            }}
          />
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} className="button">
          {t('common.close', 'Schließen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DicomPDFViewer;
